import { Grid, Group, LoadingOverlay } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { ActivationType, PrescriptionType } from 'models/prescription';
import i18next from 'i18next';
import { usePatient, useUserData } from 'hooks/api/useUser';
import { validateEmail, validateRequired } from 'utils/validation';
import { useCreatePrescription } from 'hooks/api/prescription/useCreatePrescription';
import { useDrawerContext } from 'context/hooks';
import { createDiscardPrescriptionModal } from 'components/Modal/ConfirmModals';
import { CreatePrescriptionFormProvider, useCreatePrescriptionForm } from 'context/FormContext';
import { buildEmailBody } from 'utils/prescription';
import { PortalButton } from 'components/Button/PortalButton';
import { showUsaFeature } from 'utils/app';
import { PatientProfile } from 'utils/models';
import { matches } from '@mantine/form';
import { FormPrescriptionSelector } from './FormPrescriptionSelector';
import { FormPrescriptionActivation } from './FormPrescriptionActivation';
import { FormPrescriptionDeactivation } from './FormPrescriptionDeactivation';

interface CreatePrescriptionProps {
  patient?: PatientProfile;
}

function optional<T>(rule: (value: T | undefined) => React.ReactNode) {
  return (value: T | undefined) => {
    if (value === undefined) return null;
    if (typeof value === 'string' && value.length === 0) return null;
    return rule(value);
  };
}

export const CreatePrescription = ({ patient }: CreatePrescriptionProps) => {
  const { t } = useTranslation();
  const patientId = patient?.id;
  const { language: currentLanguage } = i18next;
  const { data: userData, isLoading: isLoadingUser } = useUserData();
  const { data: patientData, isLoading: isLoadingPatientData } = usePatient({ patientId });
  const { mutate: createPrescription, isPending } = useCreatePrescription();
  const { setDiscardModal, closeDrawer } = useDrawerContext();

  const form = useCreatePrescriptionForm({
    initialValues: {
      reason: '',
      groupId: userData?.visibleGroupsIds.length === 1 ? userData.visibleGroupsIds[0] : '',
      prescriptionType: PrescriptionType.Free,
      packageId: '',
      activationType: patientId ? ActivationType.Immediate : ActivationType.Deferred,
      language: currentLanguage,
      shouldSendEmail: false,
      emailRecipient: '',
      emailSubject: t('prescription_mail_subject'),
      emailBody: buildEmailBody({ patientData, userData, language: currentLanguage }),
      consultedCompatibleDevices: false,
      usBillingIcdCode: undefined,
      usBillingConsent: false,
    },
    validate: {
      emailRecipient: (value, { shouldSendEmail }) => !patientData && shouldSendEmail && validateEmail(value),
      emailBody: (value, { shouldSendEmail }) => shouldSendEmail && validateRequired({ value, label: t('body_label') }),
      usBillingIcdCode: optional(
        matches(
          /^[A-TV-Z][0-9][0-9AB](?:\.[0-9A-Z]{1,4})?$/,
          t('us_billing_consent_icd_code_format_error', { icd_example: t('us_billing_consent_icd_code_placeholder') }),
        ),
      ),
    },
    onValuesChange: (values, previous) => {
      if (values.groupId !== previous.groupId && values.deactivationGroupIds?.includes(values.groupId)) {
        form.setFieldValue(
          'deactivationGroupIds',
          values.deactivationGroupIds.filter((id) => id !== values.groupId),
        );
      }
      if (form.isDirty() && !isPending) {
        setDiscardModal(createDiscardPrescriptionModal());
      }
    },
  });

  const { values, onSubmit } = form;

  const handleFormSubmit = async () => {
    createPrescription({ ...values, patientData });
    closeDrawer(false);
  };

  const isLoading = isLoadingUser || isLoadingPatientData;
  if (isLoading || !userData) return <LoadingOverlay visible />;

  return (
    <CreatePrescriptionFormProvider form={form}>
      <form onSubmit={onSubmit(handleFormSubmit)}>
        <Grid>
          <Grid.Col span={6}>
            <FormPrescriptionSelector patientId={patientId} />
          </Grid.Col>
          <Grid.Col span={6}>
            <FormPrescriptionActivation patientId={patientId} />
            {patient?.groups.find((group) => !group.expired) !== undefined && (
              <FormPrescriptionDeactivation patient={patient} />
            )}
            <Group mt={'md'} px={'md'}>
              <PortalButton
                type={'submit'}
                label={t('confirm_label')}
                disabled={
                  !values.reason ||
                  !values.packageId ||
                  !values.activationType ||
                  (showUsaFeature && !values.consultedCompatibleDevices)
                }
                testId={'button-create-prescription'}
              />
            </Group>
          </Grid.Col>
        </Grid>
      </form>
    </CreatePrescriptionFormProvider>
  );
};
